import { hierarchy, stratify } from "d3-hierarchy";
import {
  ArtworkNode,
  IArtworkSprite,
  SetData,
  SortingArtwork,
} from "../../interfaces";
import _ from "lodash";
import { forceCollide, forceSimulation, forceX, forceY } from "d3-force";

interface SimulationNode {
  artwork: SortingArtwork;
  sprite: IArtworkSprite;
  x: number;
  y: number;
  data: any;
}

export class TimelineSimulation {
  private artworks: SortingArtwork[];
  private artworkSprites: IArtworkSprite[];
  private dimensions: { width: number; height: number };
  public instance: any;
  public nodes: SimulationNode[] = [];
  public onSimulationEnd?: () => void;

  // **New Property**
  public xPositionsByYear: { [key: number]: number } = {};

  constructor(
    artworks: SortingArtwork[],
    artworkSprites: IArtworkSprite[],
    dimensions: { width: number; height: number },
  ) {
    this.artworks = artworks;
    this.artworkSprites = artworkSprites;
    this.dimensions = dimensions;
  }

  /**
   * Run beeswarm simulation sorted by start year with a more timeline-like layout
   * @param xAccessor Function to determine x-position (default: start year)
   */
  public run(xAccessor?: (artwork: SortingArtwork) => number): void {
    // Default x-accessor uses start year if not provided
    const getX = xAccessor || ((artwork: SortingArtwork) => artwork.startYear);

    // Step 1: Group artworks by year and calculate density
    const artworksByYear = _.groupBy(this.artworks, (artwork) => getX(artwork));
    const years = Object.keys(artworksByYear)
      .map(Number)
      .sort((a, b) => a - b);

    const densityByYear = years.map((year) => ({
      year,
      count: artworksByYear[year].length,
    }));

    // Step 2: Define spacing parameters
    const baseSpace = 10; // Base space allocated for each year
    const extraSpacePerArtwork = 20; // Extra space per additional artwork

    // Calculate cumulative x positions and map them to years
    let cumulativeX = 0;
    const xPositionsByYearLocal: { [key: number]: number } = {};

    densityByYear.forEach(({ year, count }) => {
      // Allocate extra space for additional artworks beyond the first
      const extraSpace = count > 1 ? (count - 1) * extraSpacePerArtwork : 0;

      // Define the allocated space for this year
      const allocatedSpace = baseSpace + extraSpace;

      // Assign the central x position for the year within its allocated space
      xPositionsByYearLocal[year] = cumulativeX + allocatedSpace / 2;

      // Update cumulativeX for the next year, ensuring strict chronological order
      cumulativeX += allocatedSpace + baseSpace * 0.2; // Adding 20% of baseSpace as padding between years
    });

    // **Assign to Public Property**
    this.xPositionsByYear = xPositionsByYearLocal;

    // Update the total width based on cumulativeX
    this.dimensions.width = cumulativeX;

    // Step 3: Assign nodes with adjusted x positions
    this.nodes = this.artworks.map((artwork, index) => {
      const year = getX(artwork);
      const artworksInYear = artworksByYear[year];
      const artworkIndex = artworksInYear.indexOf(artwork);
      const count = artworksInYear.length;

      // Spread artworks evenly within the allocated space
      const spread =
        count > 1 ? (artworkIndex - (count - 1) / 2) * extraSpacePerArtwork : 0;

      return {
        artwork,
        sprite: this.artworkSprites[index],
        x: xPositionsByYearLocal[year] + spread,
        y: this.dimensions.height / 2,
        data: null, // You can populate this if needed
      };
    });

    // Step 4: Initialize the force simulation with custom x-positions
    this.instance = forceSimulation(this.nodes)
      .force(
        "x",
        forceX<SimulationNode>()
          .x((d) => d.x)
          .strength(1), // Increased strength to strongly adhere to custom x positions
      )
      .force("y", forceY(this.dimensions.height / 2).strength(0.1))
      .force(
        "collide",
        forceCollide<SimulationNode>()
          .radius((d) => d.sprite.radius * d.sprite.scaleFactor + 10)
          .strength(1), // Strong collision to prevent overlapping
      )
      .alphaDecay(0.02) // Slower cooling for precise positioning
      .on("tick", () => {
        // Update sprite positions during simulation
        this.nodes.forEach((node) => {
          node.sprite.x = node.x;
          node.sprite.y = node.y;
        });
      })
      .on("end", () => {
        // Final position assignment after simulation ends
        this.nodes.forEach((node) => {
          node.sprite.x = node.x;
          node.sprite.y = node.y;
        });
        if (this.onSimulationEnd) {
          this.onSimulationEnd();
        }
      });
  }

  /**
   * Stop the current simulation
   */
  public stop(): void {
    if (this.instance) {
      this.instance.stop();
    }
  }
}
